import React, { useCallback, useEffect, useState } from 'react';
import { GridContainer, Grid } from 'src/shared/grid';
import { Formik, Form } from 'formik';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import 'src/features/field-sample//FieldSample.scss';
import * as Yup from 'yup';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import { alertMessages } from 'src/constants';
import { errorResponse, validateInt, ValidCharacters } from 'src/store/common';
import { createUpdateDroughtCatalogCall } from 'src/apis/mutationCatalogs';
import { getDroughtCatalogCall } from 'src/apis/getCatalogCalls';
import LoadingSpinner from 'src/shared/loading/loading';


export const validationSchema = Yup.object().shape(
  {
    droughtCatalogName: Yup.string().nullable()
      .required('Drought Catalog Name is Required'),
    kbdi_threshold: Yup.number().nullable().min(0, "KBDI Threshold must be greater than or equal to 0")
      .max(800, "KBDI Threshold must be less than or equal to 800")
      .required('KBDI Threshold is Required'),
    initial_kbdi: Yup.number().nullable().min(0, "Initial KBDI must be greater than or equal to 0")
      .max(800, "Initial KBDI must be less than or equal to 800")
      .required('Initial KBDI is Required'),
  }
);

type ReportProps ={
  setOpen: any,
  setCreateUpdateDroughtSuccess: any,
  editDrought?: any,
}
interface FormVals {
  droughtCatalogId: number|null,
  droughtCatalogName: string,

  kbdi_threshold: number|null,
  initial_kbdi: number|null,
}
export const AddDroughtCatalog: React.FC<ReportProps> = ({setOpen, setCreateUpdateDroughtSuccess, editDrought}) =>{
  const [, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const [loading,setLoading] = useState<boolean>(false)
  
  const [initVals, setInitVals] = useState<FormVals>({
    droughtCatalogId: null,
    droughtCatalogName: '',
    kbdi_threshold: null,
    initial_kbdi: null
  })

  const handleSubmit= (values:any)=>{
    (async()=>{
      try{
        setLoading(true)
        const paramsToSend = {
          id: values.droughtCatalogId,
          catalogName: values.droughtCatalogName,
          kbdiInitial: values.initial_kbdi,
          kbdiThreshold: values.kbdi_threshold,
        }
        const response:any = await createUpdateDroughtCatalogCall(paramsToSend, addAlert);
        if(response?.data.data.createUpdateDroughtCatalog.message?.length>0){
          setCreateUpdateDroughtSuccess(true)
          //save banner
          if(values.droughtCatalogId===null){
            errorResponse('', addAlert, `The Drought Catalog, ${response.data.data.createUpdateDroughtCatalog.data.catalogName}, was saved.`, true);
          }
          else{
            errorResponse('', addAlert, `The changes to the Drought Catalog, ${response.data.data.createUpdateDroughtCatalog.data.catalogName}, was saved.`, true);
          }
          
          setOpen(false)
        }
        setLoading(false)

      }
      catch(error:any){
        setLoading(false)
        console.log('error', error);
        if(error?.message?.toLowerCase().includes('unique')){
          addAlert({
            id: '',
            message: alertMessages.uniqueCatalogName,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          })
        }
        else{
          addAlert({
            id: '',
            message: alertMessages.deadCatalogCreate,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          })
        }
      }
    })();
  }
  /** Make an API call to populate the initVals when editing an drought */
  const droughtCatalogCallback = useCallback((request: any) => {
    (async () => {
      try {
        const droughtCatalogResponse: any = await getDroughtCatalogCall(request, addAlert);
          if (droughtCatalogResponse.data.length > 0) {
            let editValues = {
              droughtCatalogId: droughtCatalogResponse.data[0].id,
              droughtCatalogName: droughtCatalogResponse.data[0].name,
              kbdi_threshold: droughtCatalogResponse.data[0].kbdiThreshold,
              initial_kbdi: droughtCatalogResponse.data[0].kbdiInitial,
            }
            setInitVals(editValues);
          }

      } catch (error) {
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  /* use Effect to check and make a call to get the edit drought */
  useEffect(()=>{
      if(editDrought){
        const request = {
          id: Number(editDrought),
          perPage: 10000,
          page: 0,
          sortOrder: 'asc',
        }
        droughtCatalogCallback(request)
      }
      else{
        let initValues={
            droughtCatalogId: null,
            droughtCatalogName: '',
            kbdi_threshold:null,
            initial_kbdi:null
        }
        setInitVals(initValues);
      }

  },[addAlert, editDrought, droughtCatalogCallback]);
  
  return(
  <GridContainer className="removePadding smooth-scrollbar" style={{height: '80%', maxHeight: '95%', minHeight:'50%', overflowX: 'hidden', paddingBottom: '2rem'}}>
      {(loading) && (
        <div>
          <LoadingSpinner />
        </div>
      )}
    <Grid row gap="md" className='page-heading'>
      <Grid
        desktop={{col:12}}
        className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
          {
            editDrought ? (
              <span className="font-sans-lg text-bold">Edit Drought Catalog</span>
            ):(
              <span className="font-sans-lg text-bold">Add a New Drought Catalog</span>
            )
          }
      </Grid>
    </Grid>
    <hr style={{width: '100%', marginBottom: '2rem', marginTop: '2rem'}}></hr>

    <Formik
      initialValues={initVals}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values) }>
      {({
        values,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        isValid,
        setFieldTouched
      }) => { 
          const setIntValue = (event: any, type: string) => {
            setFieldTouched(type);
            const inputValue = event.target.value;
            const filteredValue = inputValue.replaceAll(/(?!^-)[^0-9]/g, '');
            const intValue = filteredValue === ''  ? '' : filteredValue ==='-'? '-':Number.parseInt(filteredValue, 10);
            setFieldValue(type, intValue==='-'?null: intValue);
          }

          const setValue = (event: any, type: string) => {
            setFieldTouched(type);

            if (event.target.value.length === 1 && /^\s/.test(event.target.value)) {
              return
            }
            setFieldValue(type, event.target.value);
          }
          return (
            <Form onSubmit={handleSubmit}>
                  <div className="paddingForm">
                    <Grid row desktop={{ col: 7 }}>

                          <label id={'CreateUpdatedroughtCatalogName'} htmlFor={'CreateUpdatedroughtCatalogName'}>Drought Catalog Name*</label>
                          <TextField
                              value={values.droughtCatalogName}
                              onBlur={() => setFieldTouched('droughtCatalogName')}
                              error={!!errors.droughtCatalogName && !!touched.droughtCatalogName}
                              helperText={errors.droughtCatalogName && touched.droughtCatalogName ? errors.droughtCatalogName : ''}
                              id={'CreateUpdatedroughtCatalogName'}
                              type={'string'}
                              size='medium'
                              fullWidth
                              onChange={(e: any) => {setValue(e, 'droughtCatalogName') }}
                              placeholder={'Enter Drought Catalog Name'}
                          />
                      </Grid>
                      <Grid row desktop={{ col: 7 }}>

                          <label id={'CreateUpdatekbdi_threshold'} htmlFor={'CreateUpdatekbdi_threshold'}>KBDI Threshold*</label>
                          <TextField
                              value={values.kbdi_threshold}
                              onBlur={() => setFieldTouched('kbdi_threshold')}
                              error={!!errors.kbdi_threshold && !!touched.kbdi_threshold}
                              helperText={errors.kbdi_threshold && touched.kbdi_threshold ? errors.kbdi_threshold : ''}
                              id={'CreateUpdatekbdi_threshold'}
                              type={'string'}
                              size='medium'
                              fullWidth
                              onChange={(e: any) => {setIntValue(e, 'kbdi_threshold') }}
                              placeholder={'Enter KBDI Threshold'}
                              onInput={(e) =>  validateInt(e, ValidCharacters.Numeric)}

                              InputProps={{
                                  inputProps: {
                                      min: 0,
                                      max: 800
                                  }
                              }}
                          />
                      </Grid>
                    <Grid row desktop={{ col: 7 }}>

                          <label id={'CreateUpdateinitial_kbdi'} htmlFor={'CreateUpdateinitial_kbdi'}>Initial KBDI*</label>
                          <TextField
                              value={values.initial_kbdi}
                              onBlur={() => setFieldTouched('initial_kbdi')}
                              error={!!errors.initial_kbdi && !!touched.initial_kbdi}
                              helperText={errors.initial_kbdi && touched.initial_kbdi ? errors.initial_kbdi : ''}
                              id={'CreateUpdateinitial_kbdi'}
                              type={'string'}
                              size='medium'
                              fullWidth
                              onChange={(e: any) => {setIntValue(e, 'initial_kbdi') }}
                              placeholder={'Enter Initial KBDI'}
                              onInput={(e) =>  validateInt(e, ValidCharacters.Numeric)}

                              InputProps={{
                                  inputProps: {
                                      min: 0,
                                      max: 800
                                  }
                              }}
                          />
                      </Grid>

                  </div>

              <Grid row className='modalBottomDiv'>
                <Grid desktop={{ col: 10 }} className="modalCancelDiv">
                  <Button variant="outlined" style={{ backgroundColor: 'white' }} onClick={() => { setOpen(false) }}>
                    Cancel
                  </Button>
                </Grid>
                {
                  editDrought ? (
                    <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Save
                      </Button>
                    </Grid>
                  ) : (
                    <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Add
                      </Button>
                    </Grid>
                  )
                }
              </Grid>
            </Form>
          )
        }}
    </Formik>
  </GridContainer>
  )
}